<template>
  <div class="nd">
    <!-- ── Toolbar ─────────────────────────────────────────────── -->
    <div class="nd-toolbar">
      <div class="nd-toolbar__title">
        <h3 class="mb-0">Dashboard</h3>
        <span class="nd-toolbar__range" v-if="data">{{ shortDate(filters.from) }} — {{ shortDate(filters.to) }}</span>
      </div>
      <div class="nd-toolbar__filters">
        <div class="nd-field">
          <label>From</label>
          <input type="date" v-model="filters.from" />
        </div>
        <div class="nd-field">
          <label>To</label>
          <input type="date" v-model="filters.to" />
        </div>
        <div class="nd-field">
          <label>Company</label>
          <select v-model="filters.company_id" @change="onCompanyChange">
            <option value="">All companies</option>
            <option v-for="c in options.companies" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>
        <div class="nd-field">
          <label>Salesman</label>
          <select v-model="filters.salesman_id">
            <option value="">All salesmen</option>
            <option v-for="s in options.salesmen" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
        </div>
        <div class="nd-field">
          <label>Shop</label>
          <select v-model="filters.shop_id">
            <option value="">All shops</option>
            <option v-for="sh in options.shops" :key="sh.id" :value="sh.id">{{ sh.name }}</option>
          </select>
        </div>
        <div class="nd-field">
          <label>Product</label>
          <select v-model="filters.product_id">
            <option value="">All products</option>
            <option v-for="p in filteredProducts" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>
        </div>
        <div class="nd-field">
          <label>Low stock ≤</label>
          <input type="number" min="0" v-model.number="filters.low_stock_threshold" />
        </div>
        <button class="nd-apply" @click="load" :disabled="loading">
          <span v-if="loading" class="nd-spin"></span>
          {{ loading ? 'Loading' : 'Apply' }}
        </button>
      </div>
    </div>

    <div v-if="loading && !data" class="nd-skeleton">
      <div class="nd-skeleton__card" v-for="n in 6" :key="n"></div>
    </div>

    <template v-if="data">
      <!-- ── Zone 1: KPI cards ─────────────────────────────────── -->
      <div class="nd-kpis">
        <div class="nd-kpi" v-for="k in kpiCards" :key="k.label" :style="{ '--accent': k.color }">
          <div class="nd-kpi__icon">{{ k.icon }}</div>
          <div class="nd-kpi__body">
            <p class="nd-kpi__label">{{ k.label }}</p>
            <h4 class="nd-kpi__value">{{ k.value }}</h4>
            <span v-if="k.sub" class="nd-kpi__sub">{{ k.sub }}</span>
          </div>
        </div>
      </div>

      <!-- ── Zone 2a: Money owed (its own card row) ────────────── -->
      <div class="nd-owedwrap">
        <section class="nd-kpi nd-kpi--owed" :style="{ '--accent': '#f44336' }">
          <div class="nd-kpi__icon">💵</div>
          <div class="nd-kpi__body">
            <p class="nd-kpi__label">Money owed to us</p>
            <h4 class="nd-kpi__value nd-kpi__value--xl">{{ money(data.receivables.total) }}</h4>
            <span class="nd-kpi__sub">Receivables · as of today</span>
          </div>
        </section>
      </div>

      <!-- ── Zone 2b: Top shops / salesmen ─────────────────────── -->
      <div class="nd-grid nd-grid--2">
        <section class="nd-card">
          <header class="nd-card__head">
            <h6>Top shops that owe most</h6>
            <span class="nd-badge">Top 10</span>
          </header>
          <table class="nd-table">
            <tbody>
              <tr v-for="(s, i) in data.receivables.top_shops" :key="s.shop_id">
                <td class="nd-rank">{{ Number(i) + 1 }}</td>
                <td>
                  <router-link to="/balancesheet/shop" class="nd-link">{{ s.shop_name }}</router-link>
                  <div class="nd-track"><span :style="{ width: pct(s.balance, data.receivables.top_shops, 'balance') }"></span></div>
                </td>
                <td class="nd-amt nd-amt--danger">{{ money(s.balance) }}</td>
              </tr>
              <tr v-if="!data.receivables.top_shops.length"><td colspan="3" class="nd-empty">No outstanding balances.</td></tr>
            </tbody>
          </table>
        </section>

        <section class="nd-card">
          <header class="nd-card__head">
            <h6>Top salesmen owed most</h6>
            <span class="nd-badge">Top 10</span>
          </header>
          <table class="nd-table">
            <tbody>
              <tr v-for="(sm, i) in data.receivables.top_salesmen" :key="sm.salesman_id">
                <td class="nd-rank">{{ Number(i) + 1 }}</td>
                <td>
                  {{ sm.name }}
                  <div class="nd-track"><span class="c-owed" :style="{ width: pct(sm.balance, data.receivables.top_salesmen, 'balance') }"></span></div>
                </td>
                <td class="nd-amt nd-amt--danger">{{ money(sm.balance) }}</td>
              </tr>
              <tr v-if="!data.receivables.top_salesmen.length"><td colspan="3" class="nd-empty">No outstanding balances.</td></tr>
            </tbody>
          </table>
        </section>
      </div>

      <!-- ── Zone 3: Sales / collected chart ───────────────────── -->
      <section class="nd-card nd-card--wide">
        <header class="nd-card__head">
          <h6>Daily sales &amp; collected</h6>
          <div class="nd-legend">
            <span><i class="c-sales"></i>Sales</span>
            <span><i class="c-collected"></i>Collected</span>
          </div>
        </header>
        <div class="nd-chart" v-if="data.sales.daily.length">
          <div class="nd-chart__col" v-for="d in data.sales.daily" :key="d.date">
            <div class="nd-chart__stack">
              <div class="nd-chart__bar c-sales" :style="barStyle(d.sales)" :title="'Sales ' + money(d.sales)"></div>
              <div class="nd-chart__bar c-collected" :style="barStyle(d.collected)" :title="'Collected ' + money(d.collected)"></div>
            </div>
            <p class="nd-chart__label">{{ shortDate(d.date) }}</p>
          </div>
        </div>
        <p v-else class="nd-empty">No sales in this range.</p>

        <!-- Profit on its own scale, with a zero baseline (losses go down, red) -->
        <div class="nd-pchart-head" v-if="data.sales.daily.length">
          <h6>Daily profit</h6>
          <div class="nd-legend">
            <span><i class="c-profit"></i>Profit</span>
            <span><i class="c-loss"></i>Loss</span>
          </div>
        </div>
        <div class="nd-pchart" v-if="data.sales.daily.length">
          <div class="nd-pchart__col" v-for="d in data.sales.daily" :key="'p' + d.date" :title="(d.profit < 0 ? 'Loss ' : 'Profit ') + money(d.profit)">
            <div class="nd-pchart__pos"><span :style="posBar(d.profit)"></span></div>
            <div class="nd-pchart__neg"><span :style="negBar(d.profit)"></span></div>
            <p class="nd-chart__label">{{ shortDate(d.date) }}</p>
          </div>
        </div>
      </section>

      <!-- ── Zone 3b: breakdown tables ─────────────────────────── -->
      <div class="nd-grid nd-grid--3">
        <section class="nd-card">
          <header class="nd-card__head"><h6>Sales by salesman</h6></header>
          <table class="nd-table">
            <tbody>
              <tr v-for="s in data.sales.by_salesman" :key="s.salesman_id">
                <td>{{ s.name }}<div class="nd-track"><span class="c-sales" :style="{ width: pct(s.sales, data.sales.by_salesman, 'sales') }"></span></div></td>
                <td class="nd-amt">{{ money(s.sales) }}</td>
              </tr>
              <tr v-if="!data.sales.by_salesman.length"><td colspan="2" class="nd-empty">No data.</td></tr>
            </tbody>
          </table>
        </section>

        <section class="nd-card">
          <header class="nd-card__head"><h6>Top products by profit</h6></header>
          <table class="nd-table">
            <tbody>
              <tr v-for="p in data.sales.top_products" :key="p.product_id">
                <td>{{ p.name }}<div class="nd-track"><span class="c-profit" :style="{ width: pct(p.profit, data.sales.top_products, 'profit') }"></span></div></td>
                <td class="nd-amt nd-amt--profit">{{ money(p.profit) }}</td>
              </tr>
              <tr v-if="!data.sales.top_products.length"><td colspan="2" class="nd-empty">No data.</td></tr>
            </tbody>
          </table>
        </section>

        <section class="nd-card">
          <header class="nd-card__head"><h6>Sales by company</h6></header>
          <table class="nd-table">
            <tbody>
              <tr v-for="c in data.sales.by_company" :key="c.company_id">
                <td>{{ c.name }}<div class="nd-track"><span class="c-collected" :style="{ width: pct(c.sales, data.sales.by_company, 'sales') }"></span></div></td>
                <td class="nd-amt">{{ money(c.sales) }}</td>
              </tr>
              <tr v-if="!data.sales.by_company.length"><td colspan="2" class="nd-empty">No data.</td></tr>
            </tbody>
          </table>
        </section>
      </div>

      <!-- ── Zone 4: Stock & payables ──────────────────────────── -->
      <div class="nd-grid nd-grid--3">
        <section class="nd-card">
          <header class="nd-card__head">
            <h6>Stock &amp; payables</h6>
            <span class="nd-badge nd-badge--warn">We owe</span>
          </header>
          <div class="nd-dual">
            <div>
              <p class="nd-kpi__label">Stock value on hand</p>
              <div class="nd-bignum">{{ money(data.stock.stock_value) }}</div>
            </div>
            <div>
              <p class="nd-kpi__label">Payables to companies</p>
              <div class="nd-bignum nd-bignum--warn">{{ money(data.stock.payables_total) }}</div>
            </div>
          </div>
          <table class="nd-table nd-table--tight">
            <tbody>
              <tr v-for="c in data.stock.payables" :key="c.company_id">
                <td>{{ c.name }}</td>
                <td class="nd-amt nd-amt--warn">{{ money(c.balance) }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section class="nd-card">
          <header class="nd-card__head">
            <h6>Out of stock</h6>
            <span class="nd-badge nd-badge--danger">{{ data.stock.out_of_stock.length }}</span>
          </header>
          <div class="nd-pills nd-scroll">
            <span class="nd-pill nd-pill--danger" v-for="p in data.stock.out_of_stock" :key="p.product_id">{{ p.name }}</span>
            <p v-if="!data.stock.out_of_stock.length" class="nd-empty">Everything is in stock. 🎉</p>
          </div>
        </section>

        <section class="nd-card">
          <header class="nd-card__head">
            <h6>Low stock</h6>
            <div class="nd-toggle">
              <button :class="{ on: stockUnit === 'boxes' }" @click="stockUnit = 'boxes'">Boxes</button>
              <button :class="{ on: stockUnit === 'cartons' }" @click="stockUnit = 'cartons'">Cartons</button>
            </div>
          </header>
          <ul class="nd-lowstock nd-scroll">
            <li v-for="p in data.stock.low_stock" :key="p.product_id">
              <span>{{ p.name }}</span><b>{{ fmtStock(p.quantity, p.boxes_per_carton) }}</b>
            </li>
            <li v-if="!data.stock.low_stock.length" class="nd-empty">No low-stock items.</li>
          </ul>
        </section>
      </div>

      <!-- ── Zone 5: Inventory In / Out by company (drill-down) ─── -->
      <section class="nd-card nd-card--wide">
        <header class="nd-card__head">
          <h6>Inventory In / Out by company</h6>
          <div class="nd-invtools">
            <div class="nd-legend">
              <span><i class="c-in"></i>In</span>
              <span><i class="c-out"></i>Out</span>
            </div>
            <div class="nd-toggle">
              <button :class="{ on: invUnit === 'boxes' }" @click="invUnit = 'boxes'">Boxes</button>
              <button :class="{ on: invUnit === 'value' }" @click="invUnit = 'value'">Value</button>
            </div>
          </div>
        </header>
        <p class="nd-hint">Click a company to see its inventory detail.</p>

        <div class="nd-invchart" v-if="inv.length">
          <div class="nd-invrow" v-for="c in inv" :key="c.company_id"
               :class="{ active: invDetail && invDetail.company_id === c.company_id }"
               @click="openInvDetail(c.company_id)">
            <div class="nd-invrow__name">{{ c.name }}</div>
            <div class="nd-invrow__bars">
              <div class="nd-invrow__bar" :title="'In: ' + invLabel(c, 'in')">
                <span class="c-in" :style="{ width: invW(invMetric(c, 'in')) }"></span>
                <em>{{ invLabel(c, 'in') }}</em>
              </div>
              <div class="nd-invrow__bar" :title="'Out: ' + invLabel(c, 'out')">
                <span class="c-out" :style="{ width: invW(invMetric(c, 'out')) }"></span>
                <em>{{ invLabel(c, 'out') }}</em>
              </div>
            </div>
          </div>
        </div>
        <p v-else class="nd-empty">No inventory movement in this range.</p>

        <!-- Drill-down detail -->
        <div v-if="invLoading" class="nd-empty">Loading detail…</div>
        <div v-else-if="invDetail" class="nd-invdetail">
          <div class="nd-invdetail__head">
            <h6>{{ invDetail.name }} — inventory detail</h6>
            <button class="nd-x" @click="invDetail = null">✕</button>
          </div>
          <div class="nd-invstats">
            <div class="nd-invstat"><span>In</span><b class="c-in-text">{{ fmtQty2(invDetail.totals.in_qty) }} box</b><small>{{ money(invDetail.totals.in_value) }}</small></div>
            <div class="nd-invstat"><span>Out</span><b class="c-out-text">{{ fmtQty2(invDetail.totals.out_qty) }} box</b><small>{{ money(invDetail.totals.out_value) }}</small></div>
            <div class="nd-invstat"><span>Net moved</span><b>{{ fmtQty2(invDetail.totals.net_qty) }} box</b><small>&nbsp;</small></div>
          </div>
          <div class="nd-grid nd-grid--2">
            <div>
              <h6 class="nd-subh">By product</h6>
              <div class="nd-scroll">
                <table class="nd-table">
                  <thead><tr><th>Product</th><th class="nd-amt">In</th><th class="nd-amt">Out</th><th class="nd-amt">In ₨</th></tr></thead>
                  <tbody>
                    <tr v-for="p in invDetail.by_product" :key="p.product_id">
                      <td>{{ p.name }}</td>
                      <td class="nd-amt">{{ fmtQty2(p.in_qty) }}</td>
                      <td class="nd-amt">{{ fmtQty2(p.out_qty) }}</td>
                      <td class="nd-amt">{{ money(p.in_value) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h6 class="nd-subh">By date</h6>
              <div class="nd-scroll">
                <table class="nd-table">
                  <thead><tr><th>Date</th><th class="nd-amt">In</th><th class="nd-amt">Out</th><th class="nd-amt">In ₨</th></tr></thead>
                  <tbody>
                    <tr v-for="r in invDetail.by_date" :key="r.date">
                      <td>{{ shortDate(r.date) }}</td>
                      <td class="nd-amt">{{ fmtQty2(r.in_qty) }}</td>
                      <td class="nd-amt">{{ fmtQty2(r.out_qty) }}</td>
                      <td class="nd-amt">{{ money(r.in_value) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref } from 'vue';
import DashboardService from '@/services/DashboardService';
import CompanyServices from '@/services/CompanyServices';
import SalesmanServices from '@/services/SalesmanServices';
import ProductServices from '@/services/ProductServices';
import ShopServices from '@/services/ShopServices';
import moment from 'moment';

export default defineComponent({
  name: 'DashboardNew',
  setup() {
    const loading = ref(true);
    const data = ref<any>(null);
    const filters = reactive({
      from: moment().startOf('month').format('YYYY-MM-DD'),
      to: moment().format('YYYY-MM-DD'),
      company_id: '' as string | number,
      salesman_id: '' as string | number,
      product_id: '' as string | number,
      shop_id: '' as string | number,
      low_stock_threshold: 10,
    });
    const options = reactive<{ companies: any[]; salesmen: any[]; products: any[]; shops: any[] }>({
      companies: [], salesmen: [], products: [], shops: [],
    });

    // Product dropdown narrows to the selected company (products carry company_id).
    const filteredProducts = computed(() =>
      filters.company_id
        ? options.products.filter((p: any) => String(p.company_id) === String(filters.company_id))
        : options.products
    );
    const onCompanyChange = () => {
      const stillValid = filteredProducts.value.some((p: any) => String(p.id) === String(filters.product_id));
      if (!stillValid) filters.product_id = '';
    };

    const loadOptions = async () => {
      try {
        const [c, s, p, sh] = await Promise.all([
          CompanyServices.paginateCompanies(1000),
          SalesmanServices.paginateSalesman(),
          ProductServices.getAllProducts(),
          ShopServices.getAllShops(),
        ]);
        options.companies = (c.data.data ?? c.data ?? []).map((x: any) => ({ id: x.id, name: x.name }));
        options.salesmen = (s.data.data ?? s.data ?? []).map((x: any) => ({
          id: x.id, name: x.fullName ?? `${x.first_name} ${x.last_name}`,
        }));
        options.products = (p.data.data ?? p.data ?? []).map((x: any) => ({
          id: x.id, name: x.name, company_id: x.company_id,
        }));
        options.shops = (sh.data.data ?? sh.data ?? []).map((x: any) => ({
          id: x.id, name: x.shopName ?? x.shop_name,
        }));
      } catch (e) {
        // dropdowns are non-critical — leave them empty on failure
      }
    };

    const money = (n: number) => '₨ ' + Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 });
    const shortDate = (d: string) => moment(d).format('DD MMM');

    // Stock display unit: quantities are stored in boxes; convert to cartons on demand.
    const stockUnit = ref<'boxes' | 'cartons'>('boxes');
    const fmtStock = (boxes: number, bpc: number) => {
      const b = Number(boxes) || 0;
      if (stockUnit.value === 'boxes' || !bpc || bpc <= 0) return b + ' box';
      const ctn = Math.floor(b / bpc);
      const rem = b - ctn * bpc;
      return (ctn ? ctn + ' ctn' : '') + (rem ? (ctn ? ' ' : '') + rem + ' box' : (ctn ? '' : '0 ctn'));
    };

    // Sales + collected share one scale (comparable magnitudes)
    const maxVal = computed(() => {
      if (!data.value) return 1;
      const vals = data.value.sales.daily.flatMap((d: any) => [d.sales, d.collected]);
      return Math.max(1, ...vals.map((v: number) => Math.abs(v)));
    });
    const barStyle = (v: number) => ({ height: Math.max(2, (Math.abs(v) / maxVal.value) * 150) + 'px' });

    // Profit has its own scale (much smaller than sales) with a zero baseline:
    // positive → green bar up, negative → red bar down.
    const maxProfit = computed(() => {
      if (!data.value) return 1;
      return Math.max(1, ...data.value.sales.daily.map((d: any) => Math.abs(d.profit)));
    });
    const PBAR = 62; // px half-height for the profit chart
    const posBar = (v: number) => ({ height: (v > 0 ? (v / maxProfit.value) * PBAR : 0) + 'px' });
    const negBar = (v: number) => ({ height: (v < 0 ? (Math.abs(v) / maxProfit.value) * PBAR : 0) + 'px' });

    // Inventory In/Out by company (drill-down)
    const inv = ref<any[]>([]);
    const invUnit = ref<'boxes' | 'value'>('boxes');
    const invDetail = ref<any>(null);
    const invLoading = ref(false);
    const fmtQty2 = (n: number) => Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 });
    const invMetric = (c: any, dir: 'in' | 'out') => (invUnit.value === 'boxes' ? c[dir + '_qty'] : c[dir + '_value']);
    const maxInv = computed(() =>
      Math.max(1, ...inv.value.flatMap((c) => [invMetric(c, 'in'), invMetric(c, 'out')].map((v) => Math.abs(Number(v) || 0))))
    );
    const invW = (v: number) => Math.max(0, (Math.abs(Number(v) || 0) / maxInv.value) * 100) + '%';
    const invLabel = (c: any, dir: 'in' | 'out') =>
      invUnit.value === 'boxes' ? fmtQty2(c[dir + '_qty']) + ' box' : money(c[dir + '_value']);

    const loadInventory = async () => {
      try {
        const res = await DashboardService.getInventoryCompanies({ from: filters.from, to: filters.to });
        inv.value = res.data.companies ?? [];
        // if a company is open, refresh its detail for the new range
        if (invDetail.value) openInvDetail(invDetail.value.company_id);
      } catch (e) {
        inv.value = [];
      }
    };
    const openInvDetail = async (companyId: number) => {
      invLoading.value = true;
      try {
        const res = await DashboardService.getInventoryCompanyDetail(companyId, { from: filters.from, to: filters.to });
        invDetail.value = res.data;
      } catch (e) {
        invDetail.value = null;
      } finally {
        invLoading.value = false;
      }
    };

    // relative width (0–100%) of a value against the max in its list
    const pct = (v: number, arr: any[], key: string) => {
      const max = Math.max(1, ...arr.map((r) => Math.abs(Number(r[key]) || 0)));
      return Math.max(3, (Math.abs(v) / max) * 100) + '%';
    };

    const kpiCards = computed(() => {
      if (!data.value) return [];
      const k = data.value.kpis;
      return [
        { label: 'Sales', value: money(k.sales), color: '#ab47bc', icon: '💰' },
        { label: 'Profit', value: money(k.profit), sub: k.margin_pct + '% margin', color: '#4caf50', icon: '📈' },
        { label: 'Collected', value: money(k.collected), color: '#ff9800', icon: '🧾' },
        { label: 'Outstanding', value: money(k.outstanding), sub: 'as of today', color: '#f44336', icon: '⏳' },
        { label: 'Returns', value: money(k.returns), color: '#8e24aa', icon: '↩️' },
        { label: 'Stock value', value: money(k.stock_value), sub: 'as of today', color: '#ef6c00', icon: '📦' },
      ];
    });

    const load = async () => {
      loading.value = true;
      try {
        const res = await DashboardService.getSummary({ ...filters });
        data.value = res.data;
      } finally {
        loading.value = false;
      }
      loadInventory();
    };

    onMounted(() => {
      loadOptions();
      load();
    });
    return {
      loading, data, filters, options, filteredProducts, onCompanyChange,
      stockUnit, fmtStock,
      inv, invUnit, invDetail, invLoading, invMetric, invW, invLabel, fmtQty2, openInvDetail,
      money, shortDate, barStyle, posBar, negBar, pct, kpiCards, load,
    };
  },
});
</script>

<style scoped>
.nd {
  --bg: #eef0f2;
  --card: #ffffff;
  --line: #e7e3ec;
  --ink: #333333;
  --muted: #8a8a99;
  --primary: #ab47bc;       /* site primary (purple) */
  --primary-dark: #8e24aa;  /* gradient end */
  --danger: #f44336;        /* site cancel/danger */
  --warn: #ff9800;          /* site secondary (orange) */
  --profit: #4caf50;        /* site action-edit (green) */
  --sales: #ab47bc;         /* purple */
  --collected: #ff9800;     /* orange */
  padding: 20px;
  background: var(--bg);
  min-height: 100%;
  color: var(--ink);
}

/* Toolbar */
.nd-toolbar {
  display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between;
  gap: 16px; margin-bottom: 22px;
}
.nd-toolbar__title { display: flex; align-items: baseline; gap: 12px; }
.nd-toolbar__title h3 { font-weight: 700; letter-spacing: -.3px; }
.nd-toolbar__range { color: var(--muted); font-size: 13px; font-weight: 500; }
.nd-toolbar__filters { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 10px; }
.nd-field { display: flex; flex-direction: column; gap: 3px; }
.nd-field label { font-size: 11px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: .4px; }
.nd-field input, .nd-field select {
  border: 1px solid var(--line); border-radius: 8px; padding: 7px 10px; font-size: 14px;
  background: var(--card); color: var(--ink); outline: none; transition: border-color .15s, box-shadow .15s;
}
.nd-field select { min-width: 150px; max-width: 200px; cursor: pointer; }
.nd-field input:focus, .nd-field select:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(171,71,188,.18); }
.nd-apply {
  border: none; border-radius: 8px; padding: 8px 20px; font-weight: 600; font-size: 14px; cursor: pointer;
  background: linear-gradient(60deg, var(--primary), var(--primary-dark)); color: #fff;
  display: inline-flex; align-items: center; gap: 8px;
  transition: filter .15s, opacity .15s;
}
.nd-apply:hover:not(:disabled) { filter: brightness(1.08); }
.nd-apply:disabled { opacity: .7; cursor: default; }
.nd-spin { width: 13px; height: 13px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: nd-spin .7s linear infinite; }
@keyframes nd-spin { to { transform: rotate(360deg); } }

/* KPI cards */
.nd-kpis { display: grid; grid-template-columns: repeat(6, 1fr); gap: 14px; margin-bottom: 20px; }
.nd-kpis > * { min-width: 0; }               /* prevent horizontal overflow */
.nd-kpi__value { overflow-wrap: anywhere; }
.nd-kpi {
  background: var(--card); border: 1px solid var(--line); border-radius: 14px; padding: 16px;
  display: flex; gap: 12px; align-items: flex-start; position: relative; overflow: hidden;
  transition: transform .15s, box-shadow .15s;
}
.nd-kpi::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: var(--accent); }
.nd-kpi:hover { transform: translateY(-2px); box-shadow: 0 8px 22px rgba(31,42,55,.08); }
.nd-kpi__icon {
  width: 40px; height: 40px; flex: none; border-radius: 10px; display: grid; place-items: center; font-size: 18px;
  background: color-mix(in srgb, var(--accent) 14%, transparent);
}
.nd-kpi__label { font-size: 12px; color: var(--muted); margin: 0 0 2px; font-weight: 600; }
.nd-kpi__value { font-size: 20px; font-weight: 700; margin: 0; letter-spacing: -.4px; }
.nd-kpi__sub { font-size: 11px; color: var(--muted); }

/* Grids */
.nd-grid { display: grid; gap: 16px; margin-bottom: 20px; }
.nd-grid--2-8 { grid-template-columns: 1fr 2fr; }
.nd-grid--2 { grid-template-columns: 1fr 1fr; }
.nd-grid > * { min-width: 0; }               /* let grid children shrink, no overflow */
.nd-owedwrap { margin-bottom: 20px; }
.nd-owedwrap .nd-kpi { width: 100%; }        /* full-width banner — no empty box beside it */
.nd-kpi--owed .nd-kpi__icon { width: 52px; height: 52px; font-size: 24px; }
.nd-kpi__value--xl { font-size: 28px; color: var(--danger); }
.nd-grid--3 { grid-template-columns: repeat(3, 1fr); }

/* Cards */
.nd-card { background: var(--card); border: 1px solid var(--line); border-radius: 14px; padding: 18px; }
.nd-card--wide { margin-bottom: 20px; }
.nd-card__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.nd-card__head h6 { margin: 0; font-weight: 700; font-size: 14px; }
.nd-badge { font-size: 11px; font-weight: 600; color: var(--muted); background: var(--bg); border: 1px solid var(--line); border-radius: 999px; padding: 2px 10px; }
.nd-badge--danger { color: var(--danger); background: rgba(225,87,89,.1); border-color: transparent; }
.nd-badge--warn { color: var(--warn); background: rgba(242,142,43,.12); border-color: transparent; }

.nd-bignum { font-size: 26px; font-weight: 800; letter-spacing: -.6px; margin-bottom: 12px; }
.nd-bignum--danger { color: var(--danger); }
.nd-bignum--warn { color: var(--warn); }

/* Aging bar */
.nd-aging__bar { display: flex; height: 10px; border-radius: 6px; overflow: hidden; background: var(--bg); margin-bottom: 12px; }
.nd-aging__seg { display: block; height: 100%; }
.nd-aging__seg.s1 { background: var(--profit); }
.nd-aging__seg.s2 { background: var(--warn); }
.nd-aging__seg.s3 { background: var(--danger); }
.nd-aging__legend { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px; }
.nd-aging__legend li { display: flex; align-items: center; gap: 7px; font-size: 13px; }
.nd-aging__legend b { margin-left: auto; font-weight: 700; }
.nd-aging__legend i { width: 10px; height: 10px; border-radius: 3px; }
.nd-aging__legend .s1 { background: var(--profit); }
.nd-aging__legend .s2 { background: var(--warn); }
.nd-aging__legend .s3 { background: var(--danger); }
.nd-note { font-size: 11px; color: var(--muted); margin: 10px 0 0; }

/* Tables */
.nd-table { width: 100%; border-collapse: collapse; }
.nd-table td { padding: 9px 6px; border-bottom: 1px solid var(--line); font-size: 13.5px; vertical-align: middle; }
.nd-table tr:last-child td { border-bottom: none; }
.nd-table--tight td { padding: 6px; font-size: 13px; }
.nd-rank { width: 26px; color: var(--muted); font-weight: 700; font-size: 12px; }
.nd-link { color: var(--sales); text-decoration: none; font-weight: 500; }
.nd-link:hover { text-decoration: underline; }
.nd-amt { text-align: right; font-weight: 700; white-space: nowrap; font-variant-numeric: tabular-nums; }
.nd-amt--danger { color: var(--danger); }
.nd-amt--warn { color: var(--warn); }
.nd-amt--profit { color: var(--profit); }
.nd-track { height: 4px; border-radius: 3px; background: var(--bg); margin-top: 5px; overflow: hidden; }
.nd-track span { display: block; height: 100%; background: var(--sales); border-radius: 3px; }
.nd-track .c-sales { background: var(--sales); }
.nd-track .c-profit { background: var(--profit); }
.nd-track .c-collected { background: var(--collected); }
.nd-track .c-owed { background: var(--danger); }
.nd-empty { color: var(--muted); font-size: 13px; text-align: center; padding: 14px 0; }

.nd-dual { display: flex; gap: 20px; margin-bottom: 12px; }
.nd-dual .nd-bignum { font-size: 20px; margin-bottom: 0; }

/* Chart */
.nd-chart { display: flex; align-items: flex-end; gap: 12px; overflow-x: auto; padding: 8px 2px 0; min-height: 190px; }
.nd-chart__col { display: flex; flex-direction: column; align-items: center; flex: none; }
.nd-chart__stack { display: flex; align-items: flex-end; gap: 3px; height: 160px; }
.nd-chart__bar { width: 9px; border-radius: 3px 3px 0 0; transition: opacity .15s; }
.nd-chart__bar:hover { opacity: .78; }
.nd-chart__label { font-size: 10px; margin: 6px 0 0; color: var(--muted); white-space: nowrap; }
.c-sales { background: var(--sales); }
.c-collected { background: var(--collected); }
.c-profit { background: var(--profit); }
.c-loss { background: var(--danger); }

/* Profit chart — own scale, zero baseline (positive up green, negative down red) */
.nd-pchart-head { display: flex; align-items: center; justify-content: space-between; margin: 22px 0 4px; padding-top: 14px; border-top: 1px solid var(--line); }
.nd-pchart-head h6 { margin: 0; font-weight: 700; font-size: 14px; }
.nd-pchart { display: flex; align-items: flex-start; gap: 12px; overflow-x: auto; padding: 6px 2px 0; }
.nd-pchart__col { display: flex; flex-direction: column; align-items: center; flex: none; }
.nd-pchart__pos { height: 62px; display: flex; align-items: flex-end; }      /* grows up to baseline */
.nd-pchart__neg { height: 62px; display: flex; align-items: flex-start; border-top: 1px solid var(--line); } /* baseline; grows down */
.nd-pchart__pos span, .nd-pchart__neg span { display: block; width: 10px; transition: opacity .15s; }
.nd-pchart__pos span { background: var(--profit); border-radius: 3px 3px 0 0; }
.nd-pchart__neg span { background: var(--danger); border-radius: 0 0 3px 3px; }
.nd-pchart__col:hover span { opacity: .78; }

/* Inventory In/Out by company (drill-down) */
.c-in { background: var(--profit); }
.c-out { background: var(--warn); }
.c-in-text { color: var(--profit); }
.c-out-text { color: var(--warn); }
.nd-invtools { display: flex; align-items: center; gap: 16px; }
.nd-hint { font-size: 12px; color: var(--muted); margin: 0 0 12px; }
.nd-invchart { display: flex; flex-direction: column; gap: 10px; }
.nd-invrow { display: flex; align-items: center; gap: 12px; padding: 6px 8px; border-radius: 8px; cursor: pointer; transition: background .15s; }
.nd-invrow:hover { background: var(--bg); }
.nd-invrow.active { background: color-mix(in srgb, var(--primary) 10%, transparent); outline: 1px solid color-mix(in srgb, var(--primary) 30%, transparent); }
.nd-invrow__name { width: 150px; flex: none; font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.nd-invrow__bars { flex: 1; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.nd-invrow__bar { display: flex; align-items: center; gap: 8px; }
.nd-invrow__bar span { display: block; height: 12px; border-radius: 3px; min-width: 2px; }
.nd-invrow__bar em { font-size: 11px; color: var(--muted); font-style: normal; white-space: nowrap; font-variant-numeric: tabular-nums; }

.nd-invdetail { margin-top: 18px; padding-top: 16px; border-top: 1px solid var(--line); }
.nd-invdetail__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.nd-invdetail__head h6 { margin: 0; font-weight: 700; font-size: 14px; }
.nd-x { border: none; background: var(--bg); border-radius: 6px; width: 26px; height: 26px; cursor: pointer; color: var(--muted); font-size: 13px; }
.nd-x:hover { background: var(--line); }
.nd-invstats { display: flex; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
.nd-invstat { flex: 1; min-width: 120px; background: var(--bg); border-radius: 10px; padding: 10px 12px; display: flex; flex-direction: column; }
.nd-invstat span { font-size: 11px; color: var(--muted); font-weight: 600; text-transform: uppercase; letter-spacing: .3px; }
.nd-invstat b { font-size: 17px; font-weight: 700; }
.nd-invstat small { font-size: 12px; color: var(--muted); }
.nd-subh { margin: 0 0 8px; font-size: 12.5px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: .3px; }
.nd-table thead th { font-size: 11px; color: var(--muted); font-weight: 600; text-align: left; padding: 6px; border-bottom: 1px solid var(--line); }
.nd-table thead th.nd-amt { text-align: right; }

.nd-legend { display: flex; gap: 16px; font-size: 12px; color: var(--muted); }
.nd-legend span { display: inline-flex; align-items: center; gap: 6px; }
.nd-legend i { width: 10px; height: 10px; border-radius: 3px; display: inline-block; }

/* Pills / low stock */
.nd-scroll { max-height: 230px; overflow-y: auto; }
.nd-pills { display: flex; flex-wrap: wrap; gap: 7px; }
.nd-pill { font-size: 12px; font-weight: 500; padding: 4px 10px; border-radius: 999px; background: var(--bg); border: 1px solid var(--line); }
.nd-pill--danger { color: var(--danger); background: rgba(225,87,89,.08); border-color: rgba(225,87,89,.2); }
.nd-toggle { display: inline-flex; border: 1px solid var(--line); border-radius: 7px; overflow: hidden; }
.nd-toggle button { border: none; background: var(--card); color: var(--muted); font-size: 11px; font-weight: 600; padding: 3px 10px; cursor: pointer; transition: background .15s, color .15s; }
.nd-toggle button.on { background: var(--warn); color: #fff; }
.nd-toggle button:not(.on):hover { background: var(--bg); }
.nd-lowstock { list-style: none; padding: 0; margin: 0; }
.nd-lowstock li { display: flex; justify-content: space-between; align-items: center; padding: 8px 4px; border-bottom: 1px solid var(--line); font-size: 13.5px; }
.nd-lowstock li:last-child { border-bottom: none; }
.nd-lowstock b { color: var(--warn); font-weight: 700; }

/* Skeleton */
.nd-skeleton { display: grid; grid-template-columns: repeat(6, 1fr); gap: 14px; }
.nd-skeleton__card { height: 92px; border-radius: 14px; background: linear-gradient(90deg, #eef1f6 25%, #e3e8f0 37%, #eef1f6 63%); background-size: 400% 100%; animation: nd-shine 1.3s ease infinite; }
@keyframes nd-shine { 0% { background-position: 100% 0; } 100% { background-position: -100% 0; } }

/* Responsive */
@media (max-width: 1200px) {
  .nd-kpis, .nd-skeleton { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 992px) {
  .nd-grid--3 { grid-template-columns: 1fr 1fr; }
  .nd-grid--2-8, .nd-grid--2 { grid-template-columns: 1fr; }
}
@media (max-width: 640px) {
  .nd-kpis, .nd-skeleton, .nd-grid--3 { grid-template-columns: 1fr 1fr; }
  .nd-toolbar { flex-direction: column; align-items: stretch; }
}
</style>
